import { openMeldRequestContextSchema } from "../daemon/profile-identity.js";
import { workingLanguageTagSchema } from "../runtime/working-language.js";
import { centerAgentToolSpeakerSchema } from "./center-agent-speaker.js";
import * as z$1 from "zod/v4";
//#region src/schemas/http/center-agent-member-research.ts
const trimmedNonEmptyStringSchema = z$1.string().trim().min(1);
const isoDateTimeSchema = z$1.iso.datetime({ offset: true });
const memberResearchTextSchema = z$1.string().trim().min(1).max(1e3);
const CENTER_AGENT_MEMBER_RESEARCH_COVERAGE_SOURCES = [
	"organization_directory",
	"approved_profile_report",
	"agent_activity",
	"github",
	"slack_workspace_directory",
	"slack_channel_history",
	"connected_apps"
];
const LEGACY_CENTER_AGENT_MEMBER_RESEARCH_COVERAGE_SOURCES = CENTER_AGENT_MEMBER_RESEARCH_COVERAGE_SOURCES.slice(0, 5);
const CENTER_AGENT_MEMBER_RESEARCH_COVERAGE_STATUSES = [
	"checked",
	"unavailable",
	"not_attributable"
];
const CENTER_AGENT_MEMBER_RESEARCH_EVIDENCE_SOURCES = [
	"agent_activity",
	"github",
	"slack",
	"connected_app"
];
const centerAgentMemberResearchEvidenceSourceSchema = z$1.enum(CENTER_AGENT_MEMBER_RESEARCH_EVIDENCE_SOURCES);
const centerAgentMemberResearchEvidenceItemSchema = z$1.strictObject({
	date: z$1.iso.date(),
	source: centerAgentMemberResearchEvidenceSourceSchema,
	sourceId: trimmedNonEmptyStringSchema.max(256),
	sourceName: trimmedNonEmptyStringSchema.max(120).optional(),
	sourceUrl: z$1.url().max(2048).optional(),
	text: memberResearchTextSchema
}).superRefine((item, context) => {
	if (item.source === "agent_activity" && item.sourceUrl) context.addIssue({
		code: "custom",
		message: "Agent Activity evidence uses its stable sourceId, not a URL.",
		path: ["sourceUrl"]
	});
	if (item.source === "github" && !item.sourceUrl) {
		context.addIssue({
			code: "custom",
			message: "GitHub evidence requires a direct HTTPS sourceUrl.",
			path: ["sourceUrl"]
		});
		return;
	}
	if ((item.source === "slack" || item.source === "connected_app") && !item.sourceName) context.addIssue({
		code: "custom",
		message: `${item.source === "slack" ? "Slack" : "Connected Plugin"} evidence requires a sourceName.`,
		path: ["sourceName"]
	});
	if (item.source === "github" && item.sourceUrl && new URL(item.sourceUrl).protocol !== "https:") context.addIssue({
		code: "custom",
		message: "GitHub evidence requires a direct HTTPS sourceUrl.",
		path: ["sourceUrl"]
	});
	if (item.source === "connected_app" && item.sourceUrl && new URL(item.sourceUrl).protocol !== "https:") context.addIssue({
		code: "custom",
		message: "Connected Plugin evidence sourceUrl must use HTTPS.",
		path: ["sourceUrl"]
	});
});
const centerAgentMemberResearchCoverageSourceSchema = z$1.enum(CENTER_AGENT_MEMBER_RESEARCH_COVERAGE_SOURCES);
const centerAgentMemberResearchCoverageStatusSchema = z$1.enum(CENTER_AGENT_MEMBER_RESEARCH_COVERAGE_STATUSES);
const centerAgentMemberResearchCoverageSchema = z$1.strictObject({
	evidence: memberResearchTextSchema.max(500),
	source: centerAgentMemberResearchCoverageSourceSchema,
	status: centerAgentMemberResearchCoverageStatusSchema,
	timeRange: trimmedNonEmptyStringSchema.max(120)
});
const centerAgentMemberResearchCoverageListSchema = z$1.array(centerAgentMemberResearchCoverageSchema).min(LEGACY_CENTER_AGENT_MEMBER_RESEARCH_COVERAGE_SOURCES.length).max(CENTER_AGENT_MEMBER_RESEARCH_COVERAGE_SOURCES.length).superRefine((coverage, context) => {
	const expectedSources = coverage.length === LEGACY_CENTER_AGENT_MEMBER_RESEARCH_COVERAGE_SOURCES.length ? LEGACY_CENTER_AGENT_MEMBER_RESEARCH_COVERAGE_SOURCES : CENTER_AGENT_MEMBER_RESEARCH_COVERAGE_SOURCES;
	for (const source of expectedSources) if (coverage.filter((entry) => entry.source === source).length !== 1) context.addIssue({
		code: "custom",
		message: `Research coverage must include ${source} exactly once.`
	});
	if (coverage.some((entry) => !expectedSources.includes(entry.source))) context.addIssue({
		code: "custom",
		message: "Research coverage must use either the complete legacy source set or the complete current source set."
	});
});
const centerAgentMemberResearchEvidenceBackedReportSchema = z$1.strictObject({
	collaborationGuidance: z$1.array(centerAgentMemberResearchEvidenceItemSchema).max(4),
	coverage: centerAgentMemberResearchCoverageListSchema,
	currentFocus: z$1.array(centerAgentMemberResearchEvidenceItemSchema).min(1).max(5),
	observedActivity: z$1.array(centerAgentMemberResearchEvidenceItemSchema).min(2).max(12),
	recentContributions: z$1.array(centerAgentMemberResearchEvidenceItemSchema).min(1).max(8),
	result: z$1.literal("evidence_backed"),
	observedLanguageTag: workingLanguageTagSchema.optional(),
	suggestedAboutAdditions: z$1.array(centerAgentMemberResearchEvidenceItemSchema).max(5)
}).superRefine((report, context) => {
	if (new Set(report.observedActivity.map((item) => `${item.source}:${item.sourceId}`)).size < 2) context.addIssue({
		code: "custom",
		message: "An evidence-backed report requires at least two distinct observed work items.",
		path: ["observedActivity"]
	});
});
const centerAgentMemberResearchInsufficientEvidenceReportSchema = z$1.strictObject({
	coverage: centerAgentMemberResearchCoverageListSchema,
	nextSteps: z$1.array(memberResearchTextSchema.max(400)).min(1).max(2),
	observedLanguageTag: workingLanguageTagSchema.optional(),
	result: z$1.literal("insufficient_evidence")
});
const centerAgentMemberResearchReportSchema = z$1.discriminatedUnion("result", [centerAgentMemberResearchEvidenceBackedReportSchema, centerAgentMemberResearchInsufficientEvidenceReportSchema]);
const centerAgentMemberResearchScopeSchema = z$1.enum(["member", "all"]);
const centerAgentMemberResearchBatchStatusSchema = z$1.enum([
	"queued",
	"running",
	"completed"
]);
const centerAgentMemberResearchTaskStatusSchema = z$1.enum([
	"queued",
	"running",
	"completed",
	"failed"
]);
const centerAgentMemberResearchTaskSchema = z$1.strictObject({
	id: trimmedNonEmptyStringSchema,
	subjectUserId: trimmedNonEmptyStringSchema,
	subjectDisplayName: trimmedNonEmptyStringSchema,
	position: z$1.number().int().positive(),
	status: centerAgentMemberResearchTaskStatusSchema,
	failureCode: trimmedNonEmptyStringSchema.nullable(),
	startedAt: isoDateTimeSchema.nullable(),
	finishedAt: isoDateTimeSchema.nullable()
}).superRefine((task, context) => {
	if (!(task.status === "queued" && task.startedAt === null && task.finishedAt === null && task.failureCode === null || task.status === "running" && task.startedAt !== null && task.finishedAt === null && task.failureCode === null || task.status === "completed" && task.startedAt !== null && task.finishedAt !== null && task.failureCode === null || task.status === "failed" && task.startedAt !== null && task.finishedAt !== null && task.failureCode !== null)) context.addIssue({
		code: "custom",
		message: "Task timestamps and failureCode must match the task status.",
		path: ["status"]
	});
	if (task.startedAt !== null && task.finishedAt !== null && Date.parse(task.finishedAt) < Date.parse(task.startedAt)) context.addIssue({
		code: "custom",
		message: "finishedAt must not be before startedAt.",
		path: ["finishedAt"]
	});
});
const centerAgentMemberResearchBatchSchema = z$1.strictObject({
	id: trimmedNonEmptyStringSchema,
	organizationId: trimmedNonEmptyStringSchema,
	scope: centerAgentMemberResearchScopeSchema,
	status: centerAgentMemberResearchBatchStatusSchema,
	totalCount: z$1.number().int().positive(),
	completedCount: z$1.number().int().nonnegative(),
	failedCount: z$1.number().int().nonnegative(),
	createdAt: isoDateTimeSchema,
	updatedAt: isoDateTimeSchema,
	tasks: z$1.array(centerAgentMemberResearchTaskSchema).min(1)
}).superRefine((batch, context) => {
	const completedCount = batch.tasks.filter((task) => task.status === "completed").length;
	const failedCount = batch.tasks.filter((task) => task.status === "failed").length;
	const countChecks = [
		[
			"totalCount",
			batch.totalCount,
			batch.tasks.length
		],
		[
			"completedCount",
			batch.completedCount,
			completedCount
		],
		[
			"failedCount",
			batch.failedCount,
			failedCount
		]
	];
	for (const [field, actual, expected] of countChecks) if (actual !== expected) context.addIssue({
		code: "custom",
		message: `${field} must match the task list.`,
		path: [field]
	});
	for (const [index, task] of batch.tasks.entries()) if (task.position !== index + 1) context.addIssue({
		code: "custom",
		message: "Task positions must be ordered and contiguous.",
		path: [
			"tasks",
			index,
			"position"
		]
	});
	if (batch.scope === "member" && batch.tasks.length !== 1) context.addIssue({
		code: "custom",
		message: "Single-member research must contain exactly one task.",
		path: ["tasks"]
	});
	const allQueued = batch.tasks.every((task) => task.status === "queued");
	const allTerminal = batch.tasks.every((task) => task.status === "completed" || task.status === "failed");
	if (batch.status === "queued" && !allQueued || batch.status === "running" && allQueued || batch.status === "completed" && !allTerminal) context.addIssue({
		code: "custom",
		message: "Batch status must match its task lifecycle.",
		path: ["status"]
	});
	if (Date.parse(batch.updatedAt) < Date.parse(batch.createdAt)) context.addIssue({
		code: "custom",
		message: "updatedAt must not be before createdAt.",
		path: ["updatedAt"]
	});
});
const startCenterAgentMemberResearchBodySchema = z$1.discriminatedUnion("scope", [z$1.strictObject({
	expectedOrganizationId: trimmedNonEmptyStringSchema,
	scope: z$1.literal("member"),
	subjectUserId: trimmedNonEmptyStringSchema
}), z$1.strictObject({
	expectedOrganizationId: trimmedNonEmptyStringSchema,
	scope: z$1.literal("all")
})]);
const startCenterAgentMemberResearchResponseSchema = z$1.strictObject({ batch: centerAgentMemberResearchBatchSchema });
const startCenterAgentMemberResearchRpcInputSchema = startCenterAgentMemberResearchBodySchema.and(z$1.strictObject({ context: openMeldRequestContextSchema }));
const centerAgentStartSelfMemberResearchToolRpcInputSchema = z$1.strictObject({
	context: openMeldRequestContextSchema,
	speakerMember: centerAgentToolSpeakerSchema
});
const getCenterAgentMemberResearchStatusResponseSchema = z$1.strictObject({
	organizationId: trimmedNonEmptyStringSchema,
	latestBatch: centerAgentMemberResearchBatchSchema.nullable()
});
const getCenterAgentMemberResearchStatusRpcInputSchema = z$1.strictObject({ context: openMeldRequestContextSchema });
//#endregion
export { CENTER_AGENT_MEMBER_RESEARCH_COVERAGE_SOURCES, CENTER_AGENT_MEMBER_RESEARCH_COVERAGE_STATUSES, CENTER_AGENT_MEMBER_RESEARCH_EVIDENCE_SOURCES, centerAgentMemberResearchBatchSchema, centerAgentMemberResearchBatchStatusSchema, centerAgentMemberResearchCoverageSchema, centerAgentMemberResearchCoverageSourceSchema, centerAgentMemberResearchCoverageStatusSchema, centerAgentMemberResearchEvidenceItemSchema, centerAgentMemberResearchEvidenceSourceSchema, centerAgentMemberResearchReportSchema, centerAgentMemberResearchScopeSchema, centerAgentMemberResearchTaskSchema, centerAgentMemberResearchTaskStatusSchema, centerAgentStartSelfMemberResearchToolRpcInputSchema, getCenterAgentMemberResearchStatusResponseSchema, getCenterAgentMemberResearchStatusRpcInputSchema, startCenterAgentMemberResearchBodySchema, startCenterAgentMemberResearchResponseSchema, startCenterAgentMemberResearchRpcInputSchema };

//# sourceMappingURL=center-agent-member-research.js.map